import React, { useEffect, useMemo, useState } from "react";
import { Alert, Box, CircularProgress, Typography } from "@mui/material";
import AccountTreeOutlinedIcon from "@mui/icons-material/AccountTreeOutlined";
import { useLocation } from "react-router-dom";
import NetworkTab from "./NetworkTab";
import FacilityNetworkPage from "./FacilityNetworkPage";
import { getNetwork, type NetworkRecord } from "@services/networkManagement";
import { networkRecordToFacilityRow } from "@utils/networkFacilityRows";

const panelSx = {
  backgroundColor: "#fdffff",
  minHeight: "90vh",
  borderRadius: "12px",
  border: "1px solid #99d4e1",
  boxShadow: "inset 8px 2px 58.4px 0px rgba(191,242,255,0.2)",
  overflow: "hidden",
};

export default function NetworkPage() {
  const location = useLocation();
  const routeState = location.state as {
    networkId?: number | null;
    networkDashboardView?: "operational" | "executive";
  } | null;
  const routeNetworkId = routeState?.networkId;
  const dashboardView = routeState?.networkDashboardView ?? "operational";

  const [loading, setLoading] = useState(routeNetworkId != null);
  const [networkRecord, setNetworkRecord] = useState<NetworkRecord | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);

  useEffect(() => {
    if (routeNetworkId == null) {
      setNetworkRecord(null);
      setLoadError(null);
      setLoading(false);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        setLoadError(null);
        const rec = await getNetwork(routeNetworkId);
        if (!cancelled) setNetworkRecord(rec);
      } catch (err) {
        if (!cancelled) {
          setNetworkRecord(null);
          setLoadError(err instanceof Error ? err.message : "Failed to load network");
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [routeNetworkId]);

  const facilityRow = useMemo(
    () => (networkRecord ? networkRecordToFacilityRow(networkRecord) : null),
    [networkRecord],
  );

  if (routeNetworkId == null) {
    return <FacilityNetworkPage />;
  }

  return (
    <Box sx={panelSx}>
      <Box sx={{ px: 3, py: 3 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 1.5 }}>
          <AccountTreeOutlinedIcon sx={{ color: "#262626", fontSize: 24 }} />
          <Typography sx={{ color: "#262626", fontSize: 26, fontWeight: 500 }}>
            {dashboardView === "executive" ? "Network · Executive Summary" : "Network"}
          </Typography>
        </Box>

        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
            <CircularProgress sx={{ color: "#0a829d" }} />
          </Box>
        ) : loadError ? (
          <Alert severity="error">{loadError}</Alert>
        ) : facilityRow ? (
          <NetworkTab facility={facilityRow} isTabActive />
        ) : (
          <Alert severity="warning">Network not found.</Alert>
        )}
      </Box>
    </Box>
  );
}
